// src/pages/Cameras/enhance_components/GridLayoutManager.jsx
// ✅ COMPLETE with Beautiful Onboarding UX

import { useMemo, useEffect, useState } from "react";
import { FiCornerDownRight, FiEye, FiFileText, FiEdit, FiTrash2, FiMonitor, FiGrid, FiZap } from "react-icons/fi";
import LiveStreamCard from "./LiveStreamCard";
import { useTheme } from "../../../context/ThemeContext";
import CameraThumbnail from "./CameraThumbnail";

export default function GridLayoutManager({
  cameras,
  streamStatus,
  layout,
  focusedCamera,
  setFocusedCamera,
  updateStreamStatus,
  onEditCamera,
  onDeleteCamera,
  canManageCameras,
  onViewLogs,
}) {

useEffect(() => {
  console.log('🎯 focusedCamera changed:', {
    focusedCamera: focusedCamera?.id,
    cameraName: focusedCamera?.name,
    layout: layout
  });
}, [focusedCamera]);

  const { theme } = useTheme();
  
  // Drag and drop state for spotlight mode
  const [draggedCamera, setDraggedCamera] = useState(null);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  // Dismiss first load hints after 5 seconds
  useEffect(() => {
    const timer = setTimeout(() => setIsFirstLoad(false), 5000);
    return () => clearTimeout(timer);
  }, []);

  // Layout configurations
  const layoutConfig = useMemo(() => {
    switch (layout) {
      case "spotlight":
        return {
          mainClass: "grid grid-cols-1 lg:grid-cols-4 gap-3",
          mainCamera: focusedCamera,
          thumbnails: cameras
            .filter(c => c.id !== focusedCamera?.id)
            .slice(0, 7),
        };

      case "grid-4":
        return {
          mainClass: "grid grid-cols-1 sm:grid-cols-2 gap-3",
          cameras: cameras.slice(0, 4),
        };
      case "grid-9":
        return {
          mainClass: "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3",
          cameras: cameras.slice(0, 9),
        };
      case "grid-16":
        return {
          mainClass: "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2",
          cameras: cameras.slice(0, 16),
        };
      case "grid-36":
        return {
          mainClass: "grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-1",
          cameras: cameras.slice(0, 36),
        };
      case "table":
        return {
          cameras: cameras,
          isTable: true,
        };
      case "list":
        return {
          mainClass: "space-y-3",
          cameras: cameras,
          isList: true,
        };
      default:
        return {
          mainClass: "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3",
          cameras: cameras,
        };
    }
  }, [layout, cameras, focusedCamera]);

  // Drag handlers
  const handleDragStart = (camera) => {
    console.log('🎬 Drag started:', camera.name);
    setDraggedCamera(camera);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (draggedCamera && layout === "spotlight") {
      console.log('📍 Dropped camera:', draggedCamera.name);
      setFocusedCamera(draggedCamera);
      setDraggedCamera(null);
    }
  };

  // Empty state
  if (!cameras || cameras.length === 0) {
    return (
      <div className={`p-12 text-center rounded-xl border ${
        theme === "dark"
          ? "bg-neutral-750 border-neutral-700"
          : "bg-white border-gray-200"
      }`}>
        <div className="text-gray-400 mb-4">
          <svg className="mx-auto h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold mb-2">No Cameras Available</h3>
        <p className="text-sm text-gray-500">
          {canManageCameras 
            ? "Add your first camera to start monitoring" 
            : "No cameras have been assigned to this site"}
        </p>
      </div>
    );
  }

  // ✅ DEFAULT VIEW - Beautiful Onboarding Experience
  if (!focusedCamera && (!layout || layout === "thumbnails")) {
    console.log('🖼️ Rendering thumbnail grid with onboarding');
    
    return (
      <div className="space-y-6 animate-fadeIn">
        {/* 🎨 HERO SECTION - Clear Instructions */}
        <div className={`relative overflow-hidden rounded-2xl border-2 ${
          theme === "dark" 
            ? "bg-gradient-to-br from-blue-500/10 via-purple-500/5 to-transparent border-blue-500/20" 
            : "bg-gradient-to-br from-blue-50 via-purple-50 to-white border-blue-200"
        }`}>
          <div className="p-8 text-center relative z-10">
            {/* Animated camera icon */}
            <div className="flex justify-center mb-6">
              <div className={`relative p-6 rounded-2xl ${
                theme === "dark" ? "bg-blue-500/20" : "bg-blue-100"
              }`}>
                <FiMonitor className="w-16 h-16 text-blue-500" />
                <div className="absolute -top-1 -right-1 w-6 h-6 bg-green-500 rounded-full animate-pulse" />
              </div>
            </div>

            {/* Title */}
            <h2 className={`text-3xl font-bold mb-3 ${
              theme === "dark" ? "text-white" : "text-gray-900"
            }`}>
              {cameras.length} Camera{cameras.length !== 1 ? 's' : ''} Ready to Monitor
            </h2>
            
            <p className={`text-lg mb-8 max-w-2xl mx-auto ${
              theme === "dark" ? "text-gray-300" : "text-gray-600"
            }`}>
              Select any camera below to start live monitoring. 
              <span className="font-semibold text-blue-500"> Zero bandwidth used until you view.</span>
            </p>

            {/* Quick Actions Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mx-auto mb-6">
              {/* Action 1: Click */}
              <div className={`p-5 rounded-xl backdrop-blur-sm transition-all hover:scale-105 ${
                theme === "dark" ? "bg-white/5 hover:bg-white/10" : "bg-white/70 hover:bg-white"
              }`}>
                <div className="text-4xl mb-3">👆</div>
                <div className={`font-bold mb-2 text-lg ${
                  theme === "dark" ? "text-white" : "text-gray-900"
                }`}>
                  Click to View
                </div>
                <div className={`text-sm ${
                  theme === "dark" ? "text-gray-400" : "text-gray-600"
                }`}>
                  Tap any camera thumbnail to watch live
                </div>
              </div>

              {/* Action 2: Drag */}
              <div className={`p-5 rounded-xl backdrop-blur-sm transition-all hover:scale-105 ${
                theme === "dark" ? "bg-white/5 hover:bg-white/10" : "bg-white/70 hover:bg-white"
              }`}>
                <div className="text-4xl mb-3">🖱️</div>
                <div className={`font-bold mb-2 text-lg ${
                  theme === "dark" ? "text-white" : "text-gray-900"
                }`}>
                  Drag & Drop
                </div>
                <div className={`text-sm ${
                  theme === "dark" ? "text-gray-400" : "text-gray-600"
                }`}>
                  Drag cameras to switch spotlight view
                </div>
              </div>

              {/* Action 3: Grid */}
              <div className={`p-5 rounded-xl backdrop-blur-sm transition-all hover:scale-105 ${
                theme === "dark" ? "bg-white/5 hover:bg-white/10" : "bg-white/70 hover:bg-white"
              }`}>
                <div className="text-4xl mb-3">📺</div>
                <div className={`font-bold mb-2 text-lg ${
                  theme === "dark" ? "text-white" : "text-gray-900"
                }`}>
                  Monitor Wall
                </div>
                <div className={`text-sm ${
                  theme === "dark" ? "text-gray-400" : "text-gray-600"
                }`}>
                  Use layout menu for multi-camera grids
                </div>
              </div>
            </div>

            {/* Performance Badge */}
            <div className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold ${
              theme === "dark" 
                ? "bg-green-500/10 text-green-400 border border-green-500/20" 
                : "bg-green-50 text-green-700 border border-green-200"
            }`}>
              <FiZap className="w-4 h-4" />
              <span>Enterprise Performance - Streams only what you watch</span>
            </div>
          </div>

          {/* Background decoration */}
          <div className="absolute inset-0 opacity-10 pointer-events-none">
            <div className="absolute top-0 left-0 w-72 h-72 bg-blue-500 rounded-full blur-3xl" />
            <div className="absolute bottom-0 right-0 w-72 h-72 bg-purple-500 rounded-full blur-3xl" />
          </div>
        </div>

        {/* 📸 CAMERA GRID - Enhanced Thumbnails */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-lg font-semibold flex items-center gap-2 ${
              theme === "dark" ? "text-white" : "text-gray-900"
            }`}>
              <FiGrid className="w-5 h-5" />
              All Cameras
            </h3>
            <span className={`text-sm ${
              theme === "dark" ? "text-gray-400" : "text-gray-600"
            }`}>
              {cameras.length} total
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {cameras.map((cam, index) => (
              <CameraThumbnail
                key={cam.id}
                camera={cam}
                draggable
                onClick={() => {
                  console.log('👆 Thumbnail clicked:', cam.name);
                  setFocusedCamera(cam);
                }}
                onDragStart={() => handleDragStart(cam)}
                isFirstLoad={isFirstLoad && index < 2}  // Show hint on first 2
              />
            ))}
          </div>
        </div>

        {/* 💡 PRO TIPS */}
        <div className={`rounded-xl border p-6 ${
          theme === "dark" 
            ? "bg-neutral-800/50 border-neutral-700" 
            : "bg-gray-50 border-gray-200"
        }`}>
          <div className="flex items-start gap-4">
            <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
              theme === "dark" ? "bg-blue-500/20 text-blue-400" : "bg-blue-100 text-blue-600"
            }`}>
              💡
            </div>
            <div>
              <h3 className={`font-semibold mb-3 ${
                theme === "dark" ? "text-white" : "text-gray-900"
              }`}>
                Pro Tips
              </h3>
              <ul className={`space-y-2 text-sm ${
                theme === "dark" ? "text-gray-400" : "text-gray-600"
              }`}>
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 font-bold mt-0.5">→</span>
                  <span><strong>Spotlight Mode:</strong> Perfect for monitoring one camera at a time</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 font-bold mt-0.5">→</span>
                  <span><strong>Grid Modes:</strong> Select 2x2, 3x3, 4x4, or 6x6 from the layout menu for monitor walls</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 font-bold mt-0.5">→</span>
                  <span><strong>Performance:</strong> Cameras stream ONLY when visible - browse freely with zero bandwidth</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 font-bold mt-0.5">→</span>
                  <span><strong>Quick View:</strong> Double-click any camera for fullscreen mode</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Spotlight Layout with Drag & Drop
  if (layout === "spotlight" || (focusedCamera && !layout)) {
    console.log('🎯 Rendering spotlight mode, focused:', focusedCamera?.name);
    
    return (
      <div className={layoutConfig.mainClass}>
        {/* Main Camera (Large) - Drop Zone - ONLY THIS STREAMS */}
        {layoutConfig.mainCamera ? (
          <div 
            className="lg:col-span-3 relative"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            {/* Drag indicator */}
            {draggedCamera && (
              <div className={`absolute inset-0 rounded-xl border-4 border-dashed border-blue-500 bg-blue-500/10 backdrop-blur-sm flex items-center justify-center z-10 pointer-events-none ${
                theme === "dark" ? "bg-blue-500/20" : "bg-blue-500/10"
              }`}>
                <div className="text-center text-blue-600 dark:text-blue-400">
                  <FiCornerDownRight className="mx-auto mb-2" size={48} />
                  <div className="font-semibold text-lg">Drop to make main camera</div>
                  <div className="text-sm opacity-75 mt-1">Release to switch</div>
                </div>
              </div>
            )}
            
            <LiveStreamCard
              camera={layoutConfig.mainCamera}
              status={streamStatus[layoutConfig.mainCamera.id] || "offline"}
              size="large"
              focused={true}
              shouldPlay={true}  // ✅ ONLY focused camera streams
              onFocus={() => setFocusedCamera(layoutConfig.mainCamera)}
              updateStreamStatus={updateStreamStatus}
              onEditCamera={onEditCamera}
              onDeleteCamera={onDeleteCamera}
              canManageCameras={canManageCameras}
              onViewLogs={onViewLogs}
            />
          </div>
        ) : (
          // Placeholder when no camera focused
          <div 
            className="lg:col-span-3 relative"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <div className={`aspect-video rounded-xl border-4 border-dashed flex items-center justify-center ${
              draggedCamera
                ? "border-blue-500 bg-blue-500/10"
                : theme === "dark"
                ? "border-neutral-700 bg-neutral-800/50"
                : "border-gray-300 bg-gray-50"
            }`}>
              <div className="text-center p-8">
                <FiMonitor className={`w-16 h-16 mx-auto mb-4 ${
                  draggedCamera
                    ? "text-blue-500"
                    : theme === "dark"
                    ? "text-gray-600"
                    : "text-gray-400"
                }`} />
                <div className={`text-xl font-semibold mb-2 ${
                  theme === "dark" ? "text-gray-300" : "text-gray-700"
                }`}>
                  {draggedCamera ? "Drop Here" : "Drag a Camera Here"}
                </div>
                <div className={`text-sm ${
                  theme === "dark" ? "text-gray-500" : "text-gray-500"
                }`}>
                  {draggedCamera 
                    ? "Release to start streaming" 
                    : "Or click a thumbnail below to start"}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Thumbnails - Draggable - NO STREAMING */}
        <div className="lg:col-span-1 space-y-3">
          {/* Helper text */}
          <div className={`hidden lg:block text-xs text-center py-2 px-3 rounded-lg ${
            theme === "dark" 
              ? "bg-blue-500/10 text-blue-400 border border-blue-500/20" 
              : "bg-blue-50 text-blue-600 border border-blue-200"
          }`}>
            💡 Drag any camera to the main view
          </div>

          {layoutConfig.thumbnails.map(camera => (
            <div
              key={camera.id}
              draggable
              onDragStart={() => handleDragStart(camera)}
              onDragEnd={() => setDraggedCamera(null)}
              className={`cursor-move transition-opacity ${
                draggedCamera?.id === camera.id ? 'opacity-50' : ''
              }`}
            >
              <LiveStreamCard
                camera={camera}
                status={streamStatus[camera.id] || "offline"}
                size="thumbnail"
                focused={false}
                shouldPlay={false}  // ✅ Thumbnails DON'T stream
                onFocus={() => {
                  console.log('📺 Switching focus to:', camera.name);
                  setFocusedCamera(camera);
                }}
                updateStreamStatus={updateStreamStatus}
                onEditCamera={onEditCamera}
                onDeleteCamera={onDeleteCamera}
                canManageCameras={canManageCameras}
                onViewLogs={onViewLogs}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Table Layout - NO STREAMING
  if (layoutConfig.isTable) {
    return (
      <div className={`rounded-xl border overflow-hidden ${
        theme === "dark"
          ? "bg-neutral-750 border-neutral-700"
          : "bg-white border-gray-200"
      }`}>
        <table className="w-full text-sm">
          <thead className={theme === "dark" ? "bg-neutral-800" : "bg-gray-100"}>
            <tr>
              <th className="p-3 text-left font-semibold">Camera</th>
              <th className="p-3 text-left font-semibold">Location</th>
              <th className="p-3 text-center font-semibold">Status</th>
              <th className="p-3 text-center font-semibold">IP Address</th>
              <th className="p-3 text-right font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {layoutConfig.cameras.map((cam) => {
              const status = streamStatus[cam.id] || "offline";
              const isOnline = status === "online";
              const isInactive = !cam.is_active;

              return (
                <tr 
                  key={cam.id} 
                  className={`border-t transition-colors ${
                    theme === "dark"
                      ? "border-neutral-700 hover:bg-neutral-800/50"
                      : "border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <td className="p-3">
                    <div className="font-semibold">{cam.name || "Unnamed Camera"}</div>
                    <div className={`text-xs ${theme === "dark" ? "text-gray-400" : "text-gray-500"}`}>
                      ID: {cam.id}
                    </div>
                  </td>
                  <td className="p-3">
                    <div className={theme === "dark" ? "text-gray-300" : "text-gray-700"}>
                      {cam.location || "—"}
                    </div>
                  </td>
                  <td className="p-3 text-center">
                    <span
                      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                        isInactive
                          ? "bg-gray-500/10 text-gray-600 dark:text-gray-400"
                          : isOnline
                          ? "bg-green-500/10 text-green-700 dark:text-green-400"
                          : "bg-red-500/10 text-red-700 dark:text-red-400"
                      }`}
                    >
                      <div className={`w-1.5 h-1.5 rounded-full ${
                        isInactive
                          ? "bg-gray-500"
                          : isOnline
                          ? "bg-green-500 animate-pulse"
                          : "bg-red-500"
                      }`} />
                      {isInactive ? "Inactive" : isOnline ? "Live" : "Offline"}
                    </span>
                  </td>
                  <td className="p-3 text-center">
                    <code className={`text-xs ${theme === "dark" ? "text-gray-400" : "text-gray-600"}`}>
                      {cam.ip_address || "—"}
                    </code>
                  </td>
                  <td className="p-3">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => setFocusedCamera(cam)}
                        title="View live"
                        className={`p-2 rounded-lg transition-colors ${
                          theme === "dark"
                            ? "hover:bg-blue-500/20 text-blue-400"
                            : "hover:bg-blue-100 text-blue-600"
                        }`}>
                        <FiEye size={16} />
                      </button>

                      <button
                        onClick={() => onViewLogs && onViewLogs(cam)}
                        title="View logs"
                        className={`p-2 rounded-lg transition-colors ${
                          theme === "dark"
                            ? "hover:bg-gray-700 text-gray-400"
                            : "hover:bg-gray-100 text-gray-600"
                        }`}>
                        <FiFileText size={16} />
                      </button>

                      {canManageCameras && (
                        <>
                          <button
                            onClick={() => onEditCamera(cam)}
                            title="Edit"
                            className={`p-2 rounded-lg transition-colors ${
                              theme === "dark"
                                ? "hover:bg-yellow-500/20 text-yellow-400"
                                : "hover:bg-yellow-100 text-yellow-600"
                            }`}>
                            <FiEdit size={16} />
                          </button>

                          <button
                            onClick={() => {
                              if (window.confirm(`Delete camera "${cam.name}"?`)) {
                                onDeleteCamera(cam.id);
                              }
                            }}
                            title="Delete"
                            className={`p-2 rounded-lg transition-colors ${
                              theme === "dark"
                                ? "hover:bg-red-500/20 text-red-400"
                                : "hover:bg-red-100 text-red-600"
                            }`}>
                            <FiTrash2 size={16} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  // List Layout - NO STREAMING
  if (layoutConfig.isList) {
    return (
      <div className={layoutConfig.mainClass}>
        {layoutConfig.cameras.map(camera => (
          <LiveStreamCard
            key={camera.id}
            camera={camera}
            status={streamStatus[camera.id] || "offline"}
            size="list"
            focused={false}
            shouldPlay={false}
            onFocus={() => setFocusedCamera(camera)}
            updateStreamStatus={updateStreamStatus}
            onEditCamera={onEditCamera}
            onDeleteCamera={onDeleteCamera}
            canManageCameras={canManageCameras}
            onViewLogs={onViewLogs}
          />
        ))}
      </div>
    );
  }

  // Grid Layouts (4, 9, 16, 36) - ALL CAMERAS STREAM
  console.log(`📺 Rendering ${layout} - all cameras will stream`);
  return (
    <div className={layoutConfig.mainClass}>
      {layoutConfig.cameras.map(camera => (
        <LiveStreamCard
          key={camera.id}
          camera={camera}
          status={streamStatus[camera.id] || "offline"}
          size="medium"
          focused={focusedCamera?.id === camera.id}
          shouldPlay={true}  // ✅ Grid views stream all cameras
          onFocus={() => setFocusedCamera(camera)}
          updateStreamStatus={updateStreamStatus}
          onEditCamera={onEditCamera}
          onDeleteCamera={onDeleteCamera}
          canManageCameras={canManageCameras}
          onViewLogs={onViewLogs}
        />
      ))}
    </div>
  );
}
